import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { getLegacyDefaultTargetThermometer, getLegacyDhwAdapterCurrentTemperature, getLegacyDhwRelayCurrentTemperature, getLegacyHeatingCircuitThermometers, getLegacyTargetThermometer, isLegacyDhwWorking, } from 'console/shared/content/device/heating-circuits/legacy/utils';
import { getConsumerControlState } from 'console/shared/content/device/heating-circuits/utils';
import { useLegacyHeatingCircuitState } from 'console/shared/hooks/device/legacy';
import { useHeatingDeviceEntitiesConfig } from 'console/shared/contexts/heating-device/types';
import { useHeatingCircuitControlValues, useHeatingCircuitMode } from '../model/hooks';
import { HeatingCircuitControlProvider } from './control';
import { HeatingCircuitContent } from './types';
import { formatTemp } from 'utils';
export const LegacyHeatingCircuitProvider = memo(function LegacyHeatingCircuitProvider(props) {
    const { config, children } = props;
    const { id, is_dhw, is_air, is_relay_dhw, is_air_pid, has_pza, is_water, thermometers_config, control_mode_config, onTargetTemperatureChange, } = config;
    const { thermostat_temps_limits, last_boiler_state, otzones_last, ot_zones_count, thermostat_target_temps, thermostat_ext_mode, thermostat_ext_modes_config, thermometers: legacy_thermometers, tempschedule, timezone, } = useLegacyHeatingCircuitState();
    const { heating_modes } = useHeatingDeviceEntitiesConfig();
    const { last_zones_state } = useMemo(() => {
        if (last_boiler_state) {
            const { zones } = last_boiler_state;
            return { last_zones_state: zones };
        }
        return {};
    }, [last_boiler_state]);
    const { target_temperature, is_manual_mode } = useMemo(() => {
        const config = thermostat_target_temps?.[id];
        if (config) {
            const { temp, manual } = config;
            return { target_temperature: temp, is_manual_mode: manual };
        }
        return { target_temperature: 0, is_manual_mode: false };
    }, [id, thermostat_target_temps?.[id]]);
    const mode_id = thermostat_ext_mode;
    const [legacy_target_thermometer, default_target_thermometer] = useMemo(() => getLegacyDefaultTargetThermometer(id, mode_id, {
        thermostat_ext_mode,
        thermostat_ext_modes_config,
        thermometers: legacy_thermometers,
        tempschedule,
        timezone,
    }), [
        id,
        mode_id,
        thermostat_ext_mode,
        thermostat_ext_modes_config,
        legacy_thermometers,
        tempschedule,
        timezone,
    ]);
    const default_thermometers = useMemo(() => getLegacyHeatingCircuitThermometers(thermometers_config, legacy_thermometers), [thermometers_config, legacy_thermometers]);
    const dhw_sensor = useMemo(() => {
        const current_temperature = is_relay_dhw
            ? getLegacyDhwRelayCurrentTemperature(default_thermometers, is_water)
            : getLegacyDhwAdapterCurrentTemperature(otzones_last, ot_zones_count, last_boiler_state);
        return {
            main: {
                id: 20192,
                temperature: current_temperature ?? null,
                format_temp: current_temperature
                    ? formatTemp(current_temperature)
                    : null,
                name: null,
                work_fine: true,
            },
            reserve: {
                id: null,
                temperature: null,
                format_temp: null,
                name: null,
                work_fine: null,
            },
        };
    }, [otzones_last, ot_zones_count, last_boiler_state]);
    const target_thermometer = useMemo(() => is_dhw
        ? dhw_sensor.main
        : getLegacyTargetThermometer(default_target_thermometer, default_thermometers, {
            is_water,
            is_air,
        }), [
        default_target_thermometer,
        thermometers_config,
        is_water,
        is_air,
        is_dhw,
        dhw_sensor,
    ]);
    const { min, max } = useMemo(() => {
        const limits = thermostat_temps_limits?.[id];
        if (limits) {
            return limits;
        }
        return { min: null, max: null };
    }, [thermostat_temps_limits?.[id]]);
    const zone_worktime = useMemo(() => last_zones_state?.[id]?.worktime, [last_zones_state?.[id]?.worktime]);
    const is_working = useMemo(() => (zone_worktime ? zone_worktime > 0 : false), [zone_worktime]);
    const dhw_working = useMemo(() => isLegacyDhwWorking(otzones_last, ot_zones_count, last_boiler_state), [otzones_last, ot_zones_count, last_boiler_state]);
    const heat_request = useMemo(() => {
        if (is_dhw) {
            return dhw_working;
        }
        return is_working;
    }, [is_working, dhw_working, is_dhw]);
    const summer_mode = false;
    const is_cooling = false;
    const working_mode = {
        heat_request,
        cooling: is_cooling,
    };
    const thermometers = useMemo(() => ({
        ...default_thermometers,
        dhw: dhw_sensor,
        target: target_thermometer,
    }), [default_thermometers, dhw_sensor, target_thermometer]);
    const control_mode_state = useMemo(() => getConsumerControlState({
        summer_mode,
    }), [summer_mode]);
    const control_mode = useMemo(() => (control_mode_config.mode ? control_mode_config : control_mode_state), [control_mode_state, control_mode_config]);
    const { default_min_temperature, default_max_temperature } = useMemo(() => {
        if (is_dhw) {
            return {
                default_min_temperature: min ?? 5,
                default_max_temperature: max ?? 75,
            };
        }
        if (has_pza) {
            return {
                default_min_temperature: 5,
                default_max_temperature: 35,
            };
        }
        if (is_air) {
            return {
                default_min_temperature: legacy_target_thermometer?.limits.low ?? min ?? 5,
                default_max_temperature: legacy_target_thermometer?.limits.high ?? max ?? 35,
            };
        }
        return {
            default_min_temperature: min ?? -30,
            default_max_temperature: max ?? 100,
        };
    }, [is_dhw, is_air, has_pza, min, max]);
    const { temperature_decimal_places, min_temperature, max_temperature } = useHeatingCircuitControlValues(default_min_temperature, default_max_temperature);
    const { temperature: current_temperature, name: thermometer_name } = target_thermometer;
    const { heating_mode, heating_mode_color } = useHeatingCircuitMode(mode_id, heating_modes, summer_mode);
    const state = useMemo(() => ({
        target_temperature,
        control_mode,
        min_temperature,
        heating_mode_color,
        current_temperature,
        current_temperature_str: formatTemp(current_temperature),
        thermometer_name,
        max_temperature,
        is_manual_mode,
        mode_id,
        working_mode,
        is_water,
        has_pza,
        is_dhw,
        is_air,
        is_air_pid,
        is_cooling,
        thermometers,
    }), [
        control_mode,
        target_temperature,
        current_temperature,
        thermometer_name,
        min_temperature,
        max_temperature,
        is_manual_mode,
        mode_id,
        working_mode,
        is_water,
        has_pza,
        heating_mode_color,
        is_dhw,
        is_air,
        is_air_pid,
        is_cooling,
        thermometers,
    ]);
    const heating_circuit = useMemo(() => ({
        ...{
            heating_mode,
            heating_modes,
            temperature_decimal_places,
            ...config,
        },
        ...state,
    }), [config, state, heating_mode, heating_modes, temperature_decimal_places]);
    return (_jsx(HeatingCircuitContent.Provider, { value: heating_circuit, children: _jsx(HeatingCircuitControlProvider, { onTargetTemperatureChange: onTargetTemperatureChange, heating_circuit: heating_circuit, children: children }) }));
});
